'use client'

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  useEffect,
  useSyncExternalStore,
} from 'react'
import type { User, UserRole } from '@/types/auth'
import { getSupabaseClient, resetSupabaseClient } from '@/lib/supabase/client'
import { NETWORK_CONSTANTS } from '@/lib/constants/network'
import { logger } from '@/lib/logger'
import { setSentryUserContext } from '@/lib/sentry/user'
import { ensureProfileAndFetch } from '@/contexts/auth/profileRecovery'
import { coerceUserRoles, primaryRoleFromRoles } from '@/lib/auth/roles'
import {
  clearAuthCallbackFromAddressBar,
  consumeAuthCallbackFromUrl,
} from '@/lib/auth/consumeAuthCallbackFromUrl'
import {
  clearRecoverySessionPending,
  markRecoverySessionPending,
  redirectRecoverySessionIfNeeded,
} from '@/lib/auth/recoverySession'
import { hydrateBrowserSessionAfterServerLogin } from '@/lib/auth/hydrateBrowserSessionAfterServerLogin'
import { normalizeAuthEmail } from '@/lib/auth/normalizeAuthEmail'
import { clearRolePerspectiveStorage } from '@/lib/rolePerspectiveStorage'
import { isTransientInfrastructureError } from '@/lib/errors'
import LoadingScreen from '@/components/ui/LoadingScreen'

export type LoginFailureReason =
  | 'invalid'
  | 'locked'
  | 'throttled'
  | 'validation'
  | 'unavailable'
  | 'timeout'
  | 'unknown'

export type LoginResult = { ok: true } | { ok: false; reason: LoginFailureReason }

interface AuthContextValue {
  currentUser: User | null
  login: (email: string, password: string) => Promise<LoginResult>
  updateProfile: (updates: { name: string }) => Promise<boolean>
  logout: () => Promise<void>
  isAuthenticated: boolean
  isLoading: boolean
}

const AuthContext = createContext<AuthContextValue | null>(null)
const LOGIN_TIMEOUT_MS = NETWORK_CONSTANTS.LOGIN_TIMEOUT_MS
const HYDRATE_TIMEOUT_MS =
  NETWORK_CONSTANTS.SUPABASE_AUTH_LOCK_TIMEOUT_MS + NETWORK_CONSTANTS.AUTH_HYDRATE_BUFFER_MS

function getAuthErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message
  if (
    error &&
    typeof error === 'object' &&
    'message' in error &&
    typeof error.message === 'string'
  ) {
    return error.message
  }
  return ''
}

function logAuthProviderIssue(message: string, error?: unknown, context?: Record<string, unknown>) {
  const payload =
    error !== undefined || context !== undefined
      ? { ...context, ...(error !== undefined ? { error } : {}) }
      : undefined

  if (error !== undefined && isTransientInfrastructureError(error)) {
    logger.warn(message, payload)
    return
  }
  if (payload && isTransientInfrastructureError(payload)) {
    logger.warn(message, payload)
    return
  }
  logger.error(message, payload)
}

function isInvalidSessionError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false

  const code = 'code' in error && typeof error.code === 'string' ? error.code : ''
  const message = getAuthErrorMessage(error).toLowerCase()

  return (
    code === 'refresh_token_not_found' ||
    code === 'session_not_found' ||
    message.includes('refresh token not found') ||
    message.includes('auth session missing') ||
    message.includes('session not found')
  )
}

async function clearBrokenSession(): Promise<void> {
  let supabase
  try {
    supabase = getSupabaseClient()
  } catch {
    return
  }

  // Clear browser-side auth state first (localStorage + in-memory singleton).
  await supabase.auth.signOut().catch(() => {})
  resetSupabaseClient()
  clearRecoverySessionPending()

  // Best-effort server-side cookie clear so proxy/auth routing stays in sync.
  if (typeof window !== 'undefined') {
    await fetch('/auth/signout', {
      method: 'POST',
      credentials: 'same-origin',
    }).catch(() => {})
  }
}

/** getSession() alone can return a stale local session after server sign-out. */
async function resolveTrustedBrowserSession(supabase: ReturnType<typeof getSupabaseClient>) {
  const {
    data: { session },
    error: sessionError,
  } = await supabase.auth.getSession()

  if (!session?.user || sessionError) {
    return {
      session: null as null,
      shouldClear: !!sessionError && isInvalidSessionError(sessionError),
    }
  }

  const {
    data: { user },
    error: userError,
  } = await supabase.auth.getUser()

  if (!user || userError) {
    return {
      session: null as null,
      shouldClear: true,
    }
  }

  return { session, shouldClear: false }
}

const LAST_ACTIVITY_THROTTLE_MS = 5 * 60 * 1000 // 5 minutes

function subscribeNoop(): () => void {
  return () => {}
}

function getClientSnapshot(): boolean {
  return true
}

function getServerSnapshot(): boolean {
  return false
}

async function touchLastActivity(userId: string): Promise<void> {
  const key = `last_activity_touch_${userId}`
  const last = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem(key) : null
  const now = Date.now()
  if (last && now - parseInt(last, 10) < LAST_ACTIVITY_THROTTLE_MS) return

  try {
    const supabase = getSupabaseClient()
    await supabase
      .from('profiles')
      .update({ last_activity_at: new Date().toISOString() })
      .eq('id', userId)
    if (typeof sessionStorage !== 'undefined') sessionStorage.setItem(key, String(now))
  } catch {
    // Non-fatal; auth still works
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [currentUser, setCurrentUser] = useState<User | null>(null)
  const [hydrated, setHydrated] = useState(false)
  const isClient = useSyncExternalStore(subscribeNoop, getClientSnapshot, getServerSnapshot)

  useEffect(() => {
    const supabase = (() => {
      try {
        return getSupabaseClient()
      } catch (error) {
        logger.error('[AuthProvider] Failed to initialize Supabase client', error)
        return null
      }
    })()

    if (!supabase) {
      const id = setTimeout(() => setHydrated(true), 0)
      return () => clearTimeout(id)
    }

    let cancelled = false

    const setSafeHydrated = () => {
      if (!cancelled) setHydrated(true)
    }

    const timeoutId = setTimeout(setSafeHydrated, HYDRATE_TIMEOUT_MS)

    const applySessionUser = async (session: {
      user: { id: string; email?: string | null; user_metadata?: { name?: unknown } }
    }) => {
      const profileResult = await ensureProfileAndFetch(
        supabase,
        session.user.id,
        session.user.email ?? '',
        session.user.user_metadata?.name as string | undefined
      )

      if (cancelled) return

      if (profileResult.status !== 'resolved') {
        logAuthProviderIssue('[AuthProvider] Failed to resolve profile for active session', profileResult.error, {
          userId: session.user.id,
        })
        if (!isTransientInfrastructureError(profileResult.error)) {
          setCurrentUser(null)
        }
        return
      }

      setCurrentUser(profileResult.user)
      void touchLastActivity(session.user.id)
    }

    void (async () => {
      try {
        // /auth/confirm owns email-callback consumption (hash is browser-only).
        // Elsewhere (esp. /login#access_token…&type=recovery) AuthProvider consumes.
        const onConfirmPage = window.location.pathname.startsWith('/auth/confirm')
        if (!onConfirmPage) {
          const callback = await consumeAuthCallbackFromUrl(supabase, {
            hash: window.location.hash,
            search: window.location.search,
            pathname: window.location.pathname,
          })

          if (cancelled) return

          if (callback.status === 'error') {
            logAuthProviderIssue(
              '[AuthProvider] Failed to consume auth callback from URL',
              callback.error
            )
          }

          if (callback.status === 'consumed') {
            if (
              callback.destination === '/reset-password' ||
              callback.type?.toLowerCase() === 'recovery'
            ) {
              markRecoverySessionPending()
            }

            // Navigate before clearing the hash / applying user. Clearing first let
            // /login see currentUser with no live callback and race to home instead
            // of /reset-password (recovery links looked like a normal sign-in).
            if (callback.destination !== window.location.pathname) {
              window.location.replace(callback.destination)
              return
            }

            clearAuthCallbackFromAddressBar()
            await applySessionUser(callback.session)
            if (cancelled) return
            setSafeHydrated()
            return
          }
        }

        const { session, shouldClear } = await resolveTrustedBrowserSession(supabase)

        if (cancelled) return

        if (session?.user) {
          if (redirectRecoverySessionIfNeeded(window.location.pathname)) {
            return
          }
          await applySessionUser(session)
        } else if (shouldClear) {
          await clearBrokenSession()
        }

        setSafeHydrated()
      } catch (error) {
        if (isInvalidSessionError(error)) {
          await clearBrokenSession()
        } else {
          logAuthProviderIssue('[AuthProvider] Failed to restore auth session', error)
        }
        setSafeHydrated()
      } finally {
        clearTimeout(timeoutId)
      }
    })()

    const handleAuthStateChange = async (
      event: 'SIGNED_IN' | 'SIGNED_OUT' | 'TOKEN_REFRESHED' | string,
      session: { user: { id: string; email?: string | null; user_metadata?: { name?: unknown } } } | null
    ) => {
      if (cancelled) return

      if (event === 'SIGNED_OUT' || !session?.user) {
        setCurrentUser(null)
        clearRecoverySessionPending()
        return
      }

      if (event === 'PASSWORD_RECOVERY') {
        markRecoverySessionPending()
        if (typeof window !== 'undefined' && window.location.pathname !== '/reset-password') {
          window.location.replace('/reset-password')
          return
        }
      }

      if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        if (redirectRecoverySessionIfNeeded(window.location.pathname)) {
          return
        }
        const profileResult = await ensureProfileAndFetch(
          supabase,
          session.user.id,
          session.user.email ?? '',
          session.user.user_metadata?.name as string | undefined
        )

        if (cancelled) return

        if (profileResult.status !== 'resolved') {
          logAuthProviderIssue('[AuthProvider] Failed to resolve profile after auth event', profileResult.error, {
            event,
            userId: session.user.id,
          })
          return
        }

        setCurrentUser(profileResult.user)
        void touchLastActivity(session.user.id)
      }
    }

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      // Keep callback synchronous; run async work out-of-band to avoid
      // Supabase auth deadlocks during sign-in/sign-out flows.
      void handleAuthStateChange(event, session).catch((error) => {
        logAuthProviderIssue('[AuthProvider] onAuthStateChange handler failed', error)
      })
    })

    return () => {
      cancelled = true
      clearTimeout(timeoutId)
      subscription.unsubscribe()
    }
  }, [])

  useEffect(() => {
    setSentryUserContext(
      currentUser
        ? {
            id: currentUser.id,
            role: currentUser.role,
          }
        : null
    )
  }, [currentUser])

  const login = useCallback(async (email: string, password: string): Promise<LoginResult> => {
    const normalizedEmail = normalizeAuthEmail(email)
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), LOGIN_TIMEOUT_MS)

    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ email: normalizedEmail, password }),
        signal: controller.signal,
      })

      let payload: {
        code?: string
        access_token?: string
        refresh_token?: string
      } = {}
      try {
        payload = (await res.json()) as {
          code?: string
          access_token?: string
          refresh_token?: string
        }
      } catch {
        /* plain body */
      }

      if (res.status === 429) {
        if (payload.code === 'account_locked') {
          return { ok: false, reason: 'locked' }
        }
        return { ok: false, reason: 'throttled' }
      }

      if (res.status === 400) {
        return { ok: false, reason: 'validation' }
      }

      if (res.status === 503 || payload.code === 'auth_unavailable') {
        return { ok: false, reason: 'unavailable' }
      }

      if (!res.ok) {
        return { ok: false, reason: 'invalid' }
      }

      const supabase = getSupabaseClient()
      const hydrateResult = await hydrateBrowserSessionAfterServerLogin(supabase, {
        access_token: payload.access_token ?? '',
        refresh_token: payload.refresh_token ?? '',
      })

      if (hydrateResult.status !== 'hydrated') {
        logger.error('[AuthContext] Session missing after server login', hydrateResult.error)
        return { ok: false, reason: 'unknown' }
      }

      const session = hydrateResult.session
      const profileResult = await ensureProfileAndFetch(
        supabase,
        session.user.id,
        session.user.email ?? '',
        session.user.user_metadata?.name as string | undefined
      )

      if (profileResult.status !== 'resolved') {
        logger.error('[AuthContext] Profile resolution failed after login', profileResult.error)
        return { ok: false, reason: 'unknown' }
      }

      setCurrentUser(profileResult.user)
      void touchLastActivity(session.user.id)
      return { ok: true }
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') {
        return { ok: false, reason: 'timeout' }
      }
      return { ok: false, reason: 'unknown' }
    } finally {
      clearTimeout(timeoutId)
    }
  }, [])

  const updateProfile = useCallback(async (updates: { name: string }) => {
    if (!currentUser) return false

    const nextName = updates.name.trim()
    if (!nextName) return false

    try {
      const { data, error } = await getSupabaseClient()
        .from('profiles')
        .update({ name: nextName })
        .eq('id', currentUser.id)
        .select('id, name, email, roles, patient_id')
        .single()

      if (error || !data) return false

      const row = data as {
        id: string
        email: string
        name: string
        roles: unknown
        patient_id: string | null
      }
      const safeRoles = coerceUserRoles(row.roles)
      setCurrentUser({
        id: row.id,
        email: row.email,
        name: row.name,
        roles: safeRoles,
        role: primaryRoleFromRoles(safeRoles),
        patientId: row.patient_id ?? undefined,
      })

      return true
    } catch {
      return false
    }
  }, [currentUser])

  const logout = useCallback(async () => {
    clearRolePerspectiveStorage()
    setCurrentUser(null)
    // Must clear the browser Supabase session before the server redirect.
    // Server-only sign-out left localStorage tokens behind, so /login saw
    // currentUser and document-nav'd home while cookies were gone → loop.
    await clearBrokenSession()
    const form = document.createElement('form')
    form.method = 'POST'
    form.action = '/auth/signout'
    document.body.appendChild(form)
    form.submit()
  }, [])

  const value = useMemo<AuthContextValue>(
    () => ({
      currentUser,
      login,
      updateProfile,
      logout,
      isAuthenticated: !!currentUser,
      isLoading: !hydrated,
    }),
    [currentUser, login, updateProfile, logout, hydrated]
  )

  if (!hydrated) {
    return (
      <AuthContext.Provider value={value}>
        {isClient ? <LoadingScreen label="Loading" /> : null}
      </AuthContext.Provider>
    )
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
